import { useNavigate } from 'react-router-dom';
import StickButtonsBloc from '../../blocs/common/StickButtonsBloc';
import { APP_URLS, CIVILITY_MADAME_CODE, CIVILITY_MONSIEUR_CODE, ERROR_MESSAGES } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { hasUpdateBeneficiary } from '../../utils/dataUtils';
import RecapElement from '../recap/RecapElement';
import { IApiErrors, IBeneficiaryAdditionReq, IModificationDataAfterSignature, relationType } from '../../context/model';
import moment from 'moment';
import { useState } from 'react';
import MgpFormCheckbox from '../../elements/MgpFormCheckbox/MgpFormCheckbox';
import { useFetchNomenclature } from '../../hooks/useFetchNomenclature';
import { accessJsonAttr, formatDateFR, formattedAttachmentOrganization, formattedNir, getAge, getValueNoSpace, handleKeyDown, isValidIBAN } from '../../utils/utils';
import { AxiosError } from 'axios';
import { updatePropositionAfterSignature } from '../../services/CustomerApiService';
import LoaderModal from '../../modals/common/LoaderModal';
import MgpMessage from '../../elements/MgpMessage/MgpMessage';
import ConfirmModal from '../../modals/common/ConfirmModal';
import ImageModal from '../../modals/common/ImageModal';
import MgpFormSelect from '../../elements/MgpFormSelect/MgpFormSelect';


interface IBeneficiaryFormData extends IBeneficiaryAdditionReq  {
    onModify: boolean,
}


type InfoModificationFormData = {
  iban: string;
  attachmentOrganization: string;
  beneficiaries: IBeneficiaryFormData[]
}


export default function InfoModificationFormComponent() {

  const navigate = useNavigate();

  const {
    state: { proposition, jwtToken },
    dispatch,
  } = useAppState();

  const civilityNomenclature = useFetchNomenclature("civility");

  const [modifyIban, setModifyIban] = useState<boolean>(false);
  const [modifyAttachmentOrganization, setModifyAttachmentOrganization] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [infoMsg, setInfoMsg] = useState<string | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [beneOnModifying, setBeneOnmodifying] = useState<number | null>(null);

  const validationSchema = Yup.object().shape({
    iban: Yup.string().required(ERROR_MESSAGES.requiredMsg)
        .test("is-IBAN-valid", ERROR_MESSAGES.invalidIBANMsg, (value) => {
          const valid = isValidIBAN(value as string);
          return valid;
        }),
    attachmentOrganization: Yup.string().required(ERROR_MESSAGES.requiredMsg)
        .min(9, ERROR_MESSAGES.invalidMsg)
        .max(11, ERROR_MESSAGES.invalidMsg)
        .matches(/^[0-9, ]+$/i, ERROR_MESSAGES.invalidMsg),
    beneficiaries: Yup.array()
        .of(
          Yup.object().shape({
            relationType: Yup.string()
            .when('onModify', {
              is: (onModify: boolean) => onModify,
              then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
            }),
            contact: Yup.object().when('onModify', {
              is: (onModify: boolean) => onModify,
              then: Yup.object().shape({
              civilityCode: Yup.string()
                .required(ERROR_MESSAGES.requiredMsg)
                .test(
                  'follow_civility',
                  'La valeur ne correspond pas à son numéro de sécurité sociale.',
                  function (value: string | undefined) {
                    const parent: any = this.parent;
                    const nir: string | undefined = parent?.nir;
                    const birthDate: string | undefined = parent?.birthDate;

                    if (!value || !nir || !birthDate) return true;
  
                    if (getAge(birthDate) <= 16) return true; 
       
                    const nirNoSpace = nir.replace(/\s+/g, '');
      
                    if (value === CIVILITY_MONSIEUR_CODE && /^(1|7)/.test(nirNoSpace)) return true;

                    if (value === CIVILITY_MADAME_CODE && /^(2|8)/.test(nirNoSpace)) return true;
    
                    return false;
                  }
                ),
              attachmentOrganization: Yup.string().required(ERROR_MESSAGES.requiredMsg)
                .min(9, ERROR_MESSAGES.invalidMsg)
                .max(11, ERROR_MESSAGES.invalidMsg)
                .matches(/^[0-9, ]+$/i, ERROR_MESSAGES.invalidMsg),
              })
            }),
          })
        )
    });

    let beneficiaryPropsMap = [] as IBeneficiaryFormData[];
    if(proposition?.beneficiaries && proposition.beneficiaries.length > 0) {
      beneficiaryPropsMap = proposition?.beneficiaries?.map((extractedInfo, index) => {   
        const beneficiaryProps = {
          onModify: false,
          contact: {
            ...extractedInfo.contact,
            attachmentOrganization: formattedAttachmentOrganization(extractedInfo?.contact?.attachmentOrganization),
            nir: formattedNir(extractedInfo?.contact?.nir)
          },
          relationType: extractedInfo.relationType,
          isConfirmed: extractedInfo.isConfirmed
        };
        
        return beneficiaryProps;
      });
    }

  const {
    register,
    handleSubmit, getValues, setValue, watch, clearErrors,
    formState: { errors },
  } = useForm<InfoModificationFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      iban: proposition?.iban ? proposition.iban.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim(): "",
      attachmentOrganization: proposition?.contact.attachmentOrganization ? formattedAttachmentOrganization(proposition?.contact.attachmentOrganization) : '',
      beneficiaries: beneficiaryPropsMap
    },
    resolver: yupResolver(validationSchema),
  });

  const onNextAction = (data: InfoModificationFormData) => {
    if (modifyIban || modifyAttachmentOrganization || getValues("beneficiaries").filter(bene => bene.onModify === true).length > 0) {
      return setErrorMsg("Merci de terminer vos modifications");
    }

    const dataBody = {} as IModificationDataAfterSignature;

    if ( proposition!.iban !== data.iban.replace(/ /g, '')) {
      dataBody.iban = data.iban.replace(/ /g, '');
    }

    if ( proposition!.contact.attachmentOrganization !== data.attachmentOrganization.replace(/ /g, '')) {
      dataBody.attachmentOrganization = data.attachmentOrganization.replace(/ /g, '');
    }

    const newBeneficiariesArr = data.beneficiaries.map(beneficiary => {
        return {
            relationType: beneficiary.relationType,
            contact: {...beneficiary.contact,
              attachmentOrganization : beneficiary.contact.attachmentOrganization.replace(/ /g, ''),
              nir : beneficiary.contact.nir.replace(/ /g, ''),
            },
        } as IBeneficiaryAdditionReq;
      }).map((bene, i) => {
        if (hasUpdateBeneficiary(bene, proposition!.beneficiaries[i])) {
          return bene;
        }
      }).filter(beneficiary => beneficiary) as IBeneficiaryAdditionReq[];

    
    if (newBeneficiariesArr.length > 0) {
      dataBody.beneficiaries = newBeneficiariesArr
    }

    if (!dataBody.attachmentOrganization && !dataBody.iban && !dataBody.beneficiaries) {
      return setInfoMsg('Pas de modification')
    }

    setShowModal(true);
  }

  const onPreviousAction = () => {
      dispatch({type: 'BACK_INFO_STEP', path: APP_URLS.recapPage});
      navigate({
        pathname: APP_URLS.recapPage,
        search: `?id=${proposition!.propositionId}`,
      });
  }

  const onCancelModifyIban = () => {
    setModifyIban(false);
    setValue("iban", proposition!.iban.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim())
  }

  const onCancelModifyAttachmentOrganization = () => {
    setModifyAttachmentOrganization(false);
    setValue("attachmentOrganization",  formattedAttachmentOrganization(proposition!.contact.attachmentOrganization))
  }

  const onCancelModifyBeneficiary = (i : number, isModifying: number | null) => {
    setValue(`beneficiaries.${i}.onModify`, false);
    setValue(`beneficiaries.${i}.contact`, proposition!.beneficiaries[i].contact);
    setValue(`beneficiaries.${i}.contact.nir`, formattedNir(proposition!.beneficiaries[i].contact.nir));
    setValue(`beneficiaries.${i}.contact.attachmentOrganization`, formattedAttachmentOrganization(proposition!.beneficiaries[i].contact.attachmentOrganization));
    setValue(`beneficiaries.${i}.relationType`, proposition!.beneficiaries![i].relationType);
    clearErrors('beneficiaries');
    setBeneOnmodifying(isModifying);
  }

   const onOpenModifyBeneficiary = (i : number) => {
    if (beneOnModifying !== null && beneOnModifying !== i) {
      onCancelModifyBeneficiary(beneOnModifying, i);
    } else {
      setBeneOnmodifying(i);
    }
    setValue(`beneficiaries.${i}.onModify`, true);

  }

  const handleSaveChangeBene = (i: number) => {
    setValue(`beneficiaries.${i}.onModify`, false);
    setBeneOnmodifying(null);
  }

  const onConfirmAction = async (data: InfoModificationFormData) => {
    try {
        const dataBody = {} as IModificationDataAfterSignature;

        if ( proposition!.iban !== data.iban.replace(/ /g, '')) {
          dataBody.iban = data.iban.replace(/ /g, '');
        }

        if ( proposition!.contact.attachmentOrganization !== data.attachmentOrganization?.replace(/ /g, '')) {
          dataBody.attachmentOrganization = data.attachmentOrganization?.replace(/ /g, '');
        }

        const newBeneficiariesArr = data.beneficiaries.map(beneficiary => {
            return {
                relationType: beneficiary.relationType,
                contact: {...beneficiary.contact,
                  attachmentOrganization : beneficiary.contact.attachmentOrganization?.replace(/ /g, ''),
                  nir : beneficiary.contact.nir?.replace(/ /g, ''),
                  civilityCode: beneficiary.contact.civilityCode,
                  civilityLabel: civilityNomenclature.options.find(el => el.code === beneficiary.contact.civilityCode)?.label || beneficiary.contact.civilityLabel
                },
            } as IBeneficiaryAdditionReq;
          }).map((bene, i) => {
            if (hasUpdateBeneficiary(bene, proposition!.beneficiaries[i])) {
              return bene;
            }
          }).filter(beneficiary => beneficiary) as IBeneficiaryAdditionReq[];

        
        if (newBeneficiariesArr.length > 0) {
          dataBody.beneficiaries = newBeneficiariesArr
        }

        setLoading(true);
        
        const token = jwtToken!.accessToken;

        const res = await updatePropositionAfterSignature(token, proposition!.propositionId, dataBody);
    
        dispatch({type: 'NEXT_INFO_STEP', path: APP_URLS.confirmModificationPage, proposition: res.data});
        navigate({
          pathname: APP_URLS.confirmModificationPage,
          search: `?id=${proposition!.propositionId}`,
        });
    } catch (error) {
        setLoading(false);
        setShowModal(false);
  
        const err = error as AxiosError;
        console.log(err);
        if(err.response && err.response.status === 400) {
          const errorData = err.response?.data as IApiErrors;
          setErrorMsg("Erreur: " + errorData.message);
        } else {
          setErrorMsg("Une erreur technique est survenue ");
        }
      }
  }

  const handleOpenInfoModal = (e: any, image: string) => {
    e.preventDefault();
    setImageUrl(image);
  }
  
  const handleCloseInfoModal = () => {
    setImageUrl('');
  }

  return (             
      <form style={{width: '100%'}}>
        { showModal && <ConfirmModal onConfirmAction={() => onConfirmAction(getValues())} isOpen={showModal} onCloseModal={() => setShowModal(false)} 
        text={<p className='ta-center bleu fz-lg fw-bold'>Merci de vérifier vos modifications avant de confirmer.</p>} />}
        <div>
          {errorMsg && <MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/>}
        </div>
        <div>
          {infoMsg && <MgpMessage type="info" message={infoMsg} hasCloseBtn={true}/>}
        </div>
        <div className="flex-grid bg-blanc shadow-noir p-md m-sm-desktop">
            <div className="grid-flex wrapper-edition ">
                <h2 className="card-title bleu mb-sm">
                IBAN
                </h2>
                {
                  modifyIban ? 
                    <div className="edition"  onClick={onCancelModifyIban} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>
                      <a href="#" className="link-bleu edit"  onClick={(e) => {e.preventDefault()}}>
                      Annuler</a> <i className="icon-arreter fushia fz-xs"></i>
                    </div>: 
                    <div className="edition"  onClick={(e) => setModifyIban(true)} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>
                      <a href="#" className="link-bleu edit"  onClick={(e) => {e.preventDefault()}}>
                      Modifier</a> <i className="icon-crayon fushia fz-xs"></i>
                    </div>
                } 
            </div>  
            {
              modifyIban ?
              <>
                <div className="form-row">            
                  <div className="form-group mw-50">
                    <MgpFormInput
                        label="IBAN"
                        id="iban"
                        labelFontWeight='regular'
                        register={register} 
                        errors={errors}
                        required={true}
                        placeholder=''
                        autoCapitalize='characters'

                    />
                  </div>
                </div> 
                <div className="form-row">            
                  <div className="ta-center mt-lg">
                      <a className="cta-bleu ml-x-md" onClick={handleSubmit((e) => setModifyIban(false))} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>Enregistrer</a>  
                  </div>
                </div>
              </>
             :
              <div className="form-row">            
                <div className="form-group mw-50">
                  <RecapElement id='iban' value={`${getValues('iban')?.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim()??''} `} />
                  {errors && accessJsonAttr(errors, 'iban') && <p className="error mt-sm">Merci de vérifier ce champ</p>}
                </div>
              </div>
            }
        </div>

        <div className="flex-grid bg-blanc shadow-noir p-md mt-md m-sm-desktop">
            <div className="grid-flex wrapper-edition ">
                <h2 className="card-title bleu mb-sm">
                Organisme de rattachement
                </h2>

                {
                  modifyAttachmentOrganization ? 
                    <div className="edition"  onClick={onCancelModifyAttachmentOrganization} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>
                      <a href="#" className="link-bleu edit"  onClick={(e) => {e.preventDefault()}}>
                      Annuler</a> <i className="icon-arreter fushia fz-xs"></i>
                    </div>: 
                    <div className="edition"  onClick={(e) => setModifyAttachmentOrganization(true)} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>
                      <a href="#" className="link-bleu edit"  onClick={(e) => {e.preventDefault()}}>
                      Modifier</a> <i className="icon-crayon fushia fz-xs"></i>
                    </div>
                }
            </div>  

            {
              modifyAttachmentOrganization ?
              <>
                <div className="form-row">            
                  <div className="form-group mw-50">
                    <MgpFormInput
                        label="Organisme de rattachement"
                        id="attachmentOrganization"
                        labelFontWeight='regular'
                        register={register} 
                        errors={errors}
                        required={true}
                        placeholder=''
                        regex={[/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3']}
                        maxLength={11}
                        tooltipText="L'organisme de rattachement est composé de 9 chiffres. Cliquez pour voir les instructions."
                        handleClickInfo={(e: any) => handleOpenInfoModal(e, "Image_AM_1.jpg")}
                    />
                  </div>
                </div>
                <div className="form-row">            
                  <div className="ta-center mt-lg">
                      <a className="cta-bleu ml-x-md" onClick={handleSubmit((e) => setModifyAttachmentOrganization(false))} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>Enregistrer</a>  
                  </div>
                </div>
              </>
              :
              <div className="form-row">            
                <div className="form-group mw-50">
                <RecapElement id='attachmentOrganization' value={getValues('attachmentOrganization') ? getValues('attachmentOrganization') : 'Non'} />
                {errors && accessJsonAttr(errors, 'attachmentOrganization') && <p className="error mt-sm">Ce champ est obligatoire</p>}
                </div>
              </div>

            }
        </div>

        {
            proposition!.beneficiaries && proposition!.beneficiaries.length > 0 &&
                <div className="flex-grid bg-blanc shadow-noir p-md mt-lg m-sm-desktop">
                    <div className="grid-flex wrapper-edition ">
                        <h2 className="card-title bleu mb-sm">
                            Vos ayants droit
                        </h2>
                    </div>  
                    {watch('beneficiaries').map((beneficiary, i) => 
                        <div key={i} className='mb-lg'>
                            <div className="grid-flex wrapper-edition ">
                                <h1 className='card-title mt-sm mb-xs'>
                                  {beneficiary.relationType === relationType.CONJOINT&&'Conjoint(e)'}
                                  {beneficiary.relationType === relationType.CONCUBIN&&'Concubin(e)'}
                                  {beneficiary.relationType === relationType.CHILD&&'Enfant'}  
                                  {' '}- {beneficiary.contact.lastName}  {beneficiary.contact.firstName}</h1>

                                {
                                  watch(`beneficiaries.${i}.onModify`) ? 
                                    <div className="edition"  onClick={() => onCancelModifyBeneficiary(i, null)} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>
                                      <a href="#" className="link-bleu edit"  onClick={(e) => {e.preventDefault()}}>
                                      Annuler</a> <i className="icon-arreter fushia fz-xs"></i>
                                    </div>: 
                                    <div className="edition"  onClick={() => onOpenModifyBeneficiary(i)} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>
                                      <a href="#" className="link-bleu edit"  onClick={(e) => {e.preventDefault()}}>
                                      Modifier</a> <i className="icon-crayon fushia fz-xs"></i>
                                    </div>
                                }
                            </div>  

                            {
                            watch(`beneficiaries.${i}.onModify`) ?
                                <div className='p-sm' style={{border: '1px solid #144196'}}>
                                    <div className="form-row">
                                        <div className="form-group mw-33">
                                            <MgpFormInput 
                                              id={`beneficiaries.${i}.relationType`} labelFontWeight='regular'
                                              label='Lien familial'
                                              disabled
                                              value={beneficiary.relationType === relationType.CONJOINT ? 'Conjoint(e)' : beneficiary.relationType === relationType.CONCUBIN ? 'Concubin(e)'
                                                : 'Enfant'
                                              }
                                            />
                                        </div>
                                    </div>
                                                
                                    <div className="form-row">
                                        <div className="form-group mw-33">
                                          {
                                            beneficiary.relationType === relationType.CHILD ?
                                              <MgpFormSelect 
                                                id={`beneficiaries.${i}.contact.civilityCode`}
                                                labelFontWeight='regular'
                                                data={civilityNomenclature.options}
                                                register={register}
                                                errors={errors}
                                                label='Civilité'
                                                required={true}
                                                unshowEmptyOption={true}
                                              />
                                            :
                                              <MgpFormInput 
                                                id={`beneficiaries.${i}.contact.civilityCode`} 
                                                labelFontWeight='regular'
                                                label='Civilité'
                                                disabled={true}
                                                value={beneficiary?.contact?.civilityLabel?.toUpperCase()}
                                              />
                                          }
      
                                        </div>
                                        <div className="form-group mw-33">
                                            <MgpFormInput
                                                label="Prénom"
                                                id={`beneficiaries.${i}.contact.firstName`}
                                                labelFontWeight='regular'
                                                required={true}
                                                disabled
                                                value={beneficiary?.contact.firstName}
                                            />
                                        </div>
                                        <div className="form-group mw-33">
                                            <MgpFormInput
                                                label="Nom"
                                                id={`beneficiaries.${i}.contact.lastName`}
                                                labelFontWeight='regular'
                                                required={true}
                                                disabled
                                                value={beneficiary?.contact.lastName}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group mw-33">
                                        <div className="form-row">
                                            <div className="form-group">
                                            <MgpFormInput
                                                label="Date de naissance"
                                                id={`beneficiaries.${i}.contact.birthDate`}
                                                icon='calendrier'
                                                type='date'
                                                iconPosition='right'
                                                labelFontWeight='regular'
                                                disabled
                                                value={`${beneficiary?.contact.birthDate ? formatDateFR(beneficiary?.contact.birthDate) : ""}`}

                                            />
                                            </div>
                                            </div>
                                        </div>
                                        <div className="form-group mw-33">
                                            <div className="form-row">
                                                <div className="form-group mw-80">
                                                <MgpFormInput
                                                    label="N° de sécurité sociale"
                                                    id={`beneficiaries.${i}.contact.nir`}
                                                    labelFontWeight='regular'
                                                    required={true}
                                                    disabled
                                                    value={formattedNir(beneficiary!.contact.nir)}
                                                />
                                                </div>
                                                <div className="form-group mw-20">
                                                <MgpFormInput
                                                    label="Clé"
                                                    id={`beneficiaries.${i}.contact.nirKey`}
                                                    labelFontWeight='regular'
                                                    disabled
                                                    value={beneficiary?.contact.nirKey}
                                                    isBoderShort
                                                />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group mw-33">
                                        <div className="form-row">
                                            <div className="form-group">
                                            <MgpFormInput
                                                label="Organisme de rattachement"
                                                labelFontWeight='regular'
                                                id={`beneficiaries.${i}.contact.attachmentOrganization`}
                                                register={register}
                                                errors={errors}
                                                required={true}
                                                regex={[/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3']}
                                                maxLength={11}
                                                tooltipText="L'organisme de rattachement est composé de 9 chiffres. Cliquez pour voir les instructions."
                                                handleClickInfo={(e: any) => handleOpenInfoModal(e, "Image_AM_1.jpg")}
                                            />
                                            </div>
                                        </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group form-check  mt-md">
                                        <MgpFormCheckbox id={`beneficiaries.${i}.contact.alsaceMoselleRegime`} label={`Cet ayant droit releve du régime Alsace-Moselle`} register={register} errors={errors} disabled/>
                                        </div> 
                                    </div>

                                    <div className="form-row">            
                                      <div className="ta-center mt-lg">
                                          <a className="cta-bleu ml-x-md" onClick={handleSubmit(() => handleSaveChangeBene(i))} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>Enregistrer</a>  
                                      </div>
                                    </div>
                                </div>
                                :
                                <div className="form-row">      
                                    <div className="form-group mw-33">
                                        <RecapElement id='birthDate' label='Date de naissance' value={`${beneficiary?.contact.birthDate ? moment(beneficiary?.contact.birthDate).format("DD/MM/YYYY" ) : ""}`} />
                                    </div>
                                    <div className="form-group mw-33">
                                        <RecapElement id='nir' label='Numéro de sécurité sociale' value={`${formattedNir(beneficiary?.contact.nir)} ${beneficiary?.contact.nirKey}`} />
                                    </div>
                                    <div className="form-group mw-33">
                                        <RecapElement id='attachmentOrganization' label='Organisme de rattachement' value={`${formattedAttachmentOrganization(beneficiary?.contact.attachmentOrganization)??''}`} />
                                    </div>
                                </div> 
                        }
                       
                       
                    </div>
                    )}
                </div>
        } 

        {loading && <LoaderModal hasMessage={false}/>}
       
       <StickButtonsBloc showNextButton={true} showPreviousButton={true} previousAction={onPreviousAction} nextAction={handleSubmit(onNextAction)} nextButtonName='Confirmer'/>

       {imageUrl && <ImageModal isOpen={true} name={imageUrl} onCloseModal={handleCloseInfoModal}/>}
      </form>
      
  )
}
